import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const MultiSelectDropdown = ({
  value = [],
  onChange,
  options = [],
  optionLabels = {},  // Map of option value to display label (e.g. {mobile_has: "Has Mobile"})
  placeholder = "Select...",
  className = "",
  showAllOption = false,
  allOptionLabel = "All",
  usePortal = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
  // Helper to get display label — falls back to the raw value so existing
  // callers without optionLabels continue to render unchanged.
  const getLabel = (option) => optionLabels[option] || option;
  
  useEffect(() => {
    if (isOpen && usePortal && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 180)
      });
    }
  }, [isOpen, usePortal]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideContainer = containerRef.current?.contains(event.target);
      const isInsideDropdown = dropdownRef.current?.contains(event.target);
      
      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (option) => {
    if (option === "all") {
      onChange([]);
      return;
    }
    
    const newValues = selectedValues.includes(option)
      ? selectedValues.filter(v => v !== option)
      : [...selectedValues, option];
    onChange(newValues);
  };

  const getDisplayText = () => {
    if (showAllOption && selectedValues.length === 0) {
      return allOptionLabel;
    }
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      return getLabel(selectedValues[0]);
    }
    if (selectedValues.length === 2) {
      return selectedValues.map(getLabel).join(", ");
    }
    return `${selectedValues.slice(0, 2).map(getLabel).join(", ")} +${selectedValues.length - 2}`;
  };

  const isAllSelected = showAllOption && selectedValues.length === 0;

  const dropdownContent = (
    <div 
      ref={dropdownRef}
      className={cn(
        "rounded-md border bg-popover text-popover-foreground shadow-lg",
        !usePortal && "absolute mt-1 w-full min-w-[180px]"
      )}
      style={usePortal ? {
        position: 'fixed',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 99999
      } : { zIndex: 9999 }}
    >
      <div className="p-1 max-h-[300px] overflow-y-auto">
        {showAllOption && (
          <div
            onClick={() => toggleOption("all")}
            className={cn(
              "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
              "hover:bg-accent hover:text-accent-foreground",
              isAllSelected && "bg-accent"
            )}
            data-testid="multiselect-option-all"
          >
            <div className={cn(
              "mr-2 h-4 w-4 rounded border flex items-center justify-center",
              isAllSelected ? "bg-primary border-primary" : "border-input"
            )}>
              {isAllSelected && <Check className="h-3 w-3 text-primary-foreground" />}
            </div>
            {allOptionLabel}
          </div>
        )}
        {options.map((option) => {
          const isSelected = selectedValues.includes(option);
          return (
            <div
              key={option}
              onClick={() => toggleOption(option)}
              className={cn(
                "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
                "hover:bg-accent hover:text-accent-foreground",
                isSelected && "bg-accent/50"
              )}
              data-testid={`multiselect-option-${option.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={cn(
                "mr-2 h-4 w-4 rounded border flex items-center justify-center",
                isSelected ? "bg-primary border-primary" : "border-input"
              )}>
                {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              {getLabel(option)}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background",
          "placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          isOpen && "ring-1 ring-ring"
        )}
        data-testid="multiselect-trigger"
      >
        <span className={cn(
          "truncate text-left",
          selectedValues.length === 0 && !showAllOption && "text-muted-foreground"
        )}>
          {getDisplayText()}
        </span>
        <ChevronDown className={cn(
          "h-4 w-4 opacity-50 transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>
      
      {isOpen && (
        usePortal 
          ? createPortal(dropdownContent, document.body)
          : dropdownContent
      )}
    </div>
  );
};

export default MultiSelectDropdown;
